import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CalendarDays } from "lucide-react";
import { parseFarmCalendarValue } from "@/lib/farmTime";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const CAT_COLOR = {
    pig: "#F4A6A6", poultry: "#F4D35E", goat: "#B5A28C", cattle: "#A95C3E",
    duck: "#8ECAE6", fish: "#219EBC", crops: "#6A994E", inventory: "#F2C078",
    vet: "#E63946", staff: "#457B9D", finance: "#2D6A4F", maintenance: "#6C757D",
    deliveries: "#F77F00",
};

export default function CalendarPage() {
    const [events, setEvents] = useState([]);
    const [view, setView] = useState("month");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const payload = await api.get("/calendar/aggregate").then(r => r.data);
            const collected = (payload.events || []).map((event) => {
            const start = parseFarmCalendarValue(event.start);
            const end = parseFarmCalendarValue(event.end || event.start);
            if (!start || !end) return null;
            return {
                id: event.id,
                title: event.title || "Farm event",
                start,
                end,
                allDay: event.all_day !== false,
                resource: {
                    color: event.color || CAT_COLOR[event.category] || (event.type === "reminder" ? "#F77F00" : "#40916C"),
                    type: event.type,
                },
            };
            }).filter(Boolean);
            setEvents(collected);
        } catch (requestError) {
            setEvents([]);
            setError(requestError.response?.status === 403
                ? "You are not authorized to view the operational calendar."
                : "The farm calendar could not be loaded. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const eventPropGetter = (ev) => ({
        style: {
            backgroundColor: ev.resource?.color || "#40916C",
            border: "none", borderRadius: "6px", color: "#fff", fontSize: "12px",
        },
    });

    return (
        <div className="space-y-6" data-testid="calendar-page">
            <div className="flex items-center gap-3">
                <CalendarDays className="w-8 h-8 text-primary" />
                <div>
                    <div className="tiny-label">{i18n.t("batch4.0a8adac9d6d5")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch4.82f3563458a5")}</h1>
                    <p className="text-muted-foreground mt-2">{i18n.t("batch4.176628b94777")}</p>
                </div>
            </div>

            {error ? <Card className="p-4 border-destructive/40 text-destructive" role="alert" data-testid="calendar-error-state">{error}</Card> : null}
            {loading ? <Card className="p-8 text-center text-muted-foreground" data-testid="calendar-loading-state">{i18n.t("batch4.e9b1ac9b5406")}</Card> : null}
            {!loading && !error && events.length === 0 ? <Card className="p-4 text-muted-foreground" data-testid="calendar-empty-state">{i18n.t("batch4.b4c7298aaddd")}</Card> : null}
            {!loading && !error ? <Card className="p-4" data-testid="calendar-container">
                <div style={{ height: 700 }}>
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        views={["month", "week", "day", "agenda"]}
                        view={view}
                        onView={setView}
                        eventPropGetter={eventPropGetter}
                        style={{ height: "100%" }}
                    />
                </div>
            </Card> : null}

            <div className="flex flex-wrap gap-2">
                {Object.entries(CAT_COLOR).map(([k, c]) => (
                    <div key={k} className="flex items-center gap-1.5 text-xs">
                        <span className="w-3 h-3 rounded" style={{ background: c }} />
                        <span className="text-muted-foreground">{k}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
